import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Mail, Bell, Lock, ExternalLink, Cog } from "lucide-react";
import KYCCard from "@/components/KYCCard";
import { SectionBlock } from "./Channel";

/**
 * Preferences — account-level toggles. Most of these live on the /account
 * page for now; this section surfaces the toggles that impact streaming and
 * gives quick jumps to the deeper account settings.
 */
export default function Preferences() {
  return (
    <div className="space-y-6 max-w-3xl" data-testid="section-preferences">
      <SectionBlock icon={ShieldCheck} title="Identity & Payouts" color="cyan">
        <KYCCard />
      </SectionBlock>

      <SectionBlock icon={Lock} title="Security" color="pink">
        <LinkRow
          label="Password, 2FA, connected accounts"
          hint="Managed on your account page."
          to="/account"
          testid="prefs-security"
        />
      </SectionBlock>

      <SectionBlock icon={Bell} title="Notifications" color="volt">
        <LinkRow
          label="Email preferences"
          hint="Stream alerts, sub renewals, weekly summaries."
          to="/account?tab=notifications"
          testid="prefs-notifications"
        />
        <LinkRow
          label="Support tickets"
          hint="Get notified when a viewer opens a ticket for you."
          to="/dashboard/preferences"
          disabled
          testid="prefs-support-alerts"
        />
      </SectionBlock>

      <SectionBlock icon={Cog} title="Developer" color="orange">
        <LinkRow
          label="Stream keys & OBS setup"
          hint="Configure OBS ingest / stream key rotation."
          to="/dashboard/integrations"
          testid="prefs-obs"
        />
        <LinkRow
          label="API tokens & webhooks"
          hint="Programmatic access — Phase 2."
          disabled
          testid="prefs-api"
        />
      </SectionBlock>
    </div>
  );
}

function LinkRow({ label, hint, to, disabled, testid }) {
  const inner = (
    <div
      className={`flex items-center justify-between p-3 rounded border transition ${
        disabled
          ? "border-white/5 opacity-60 cursor-not-allowed"
          : "border-white/10 hover:border-[color:var(--sc-cyan)]/60 hover:bg-white/5"
      }`}
      data-testid={testid}
    >
      <div className="min-w-0">
        <div className="sc-heading text-sm font-bold text-zinc-100">{label}</div>
        <div className="text-[11px] text-zinc-500 mt-0.5">{hint}</div>
      </div>
      {disabled ? (
        <span className="text-[9px] sc-mono px-1.5 py-0.5 rounded bg-white/5 text-zinc-500">SOON</span>
      ) : (
        <ExternalLink className="w-3.5 h-3.5 text-zinc-500 group-hover:text-[color:var(--sc-cyan)]" />
      )}
    </div>
  );
  return disabled ? inner : <Link to={to} className="block group">{inner}</Link>;
}
